import { useRef } from "react";
import { Modal, Box, Typography } from "@mui/material";
import { readRemoteFile } from "react-papaparse";
import { CloudUpload } from "@mui/icons-material";

// Components
import { useNotification } from "../utils/Notification";

// Context
import { useAuth } from "../../contexts/AuthContext";
import { trackEvent } from "../utils/Utils";

// Supabase
import supabase from "../../supabaseClient";
import { useTranslation } from "react-i18next";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "80%", md: "60%", lg: "40%" },
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "24px",
  boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  overflowY: "auto",
  p: { xs: 3, md: 5 },
};

export default function ImportContacts({ open, onClose }) {
  const fileInputRef = useRef(null);

  // Components
  const { showNotification } = useNotification();

  // Context
  const { teamId } = useAuth();
  const { t } = useTranslation();

  const handleFileDrop = (e) => {
    trackEvent("importcontacts-file_drop");
    e.preventDefault();
    processFile(e.dataTransfer.files[0]);
  };

  const handleFileChange = (e) => {
    processFile(e.target.files[0]);
  };

  const processFile = (file) => {
    if (file) {
      readRemoteFile(file, {
        header: true,
        complete: (results) => {
          handleSubmit(results.data);
        },
      });
    }
  };

  const handleSubmit = async (csvData) => {
    trackEvent("importcontacts-form_submit");
    try {
      const contactsToInsert = csvData
        // Filter those who doesn't contain countrycode_id
        .filter((row) => row.countrycode_id)
        .map((row) => ({
          first_name: row.first_name || null,
          last_name: row.last_name || null,
          nickname: row.nickname || null,
          description: row.description || null,
          countrycode_id: row.countrycode_id || null,
          phone: row.phone || null,
          birthday: row.birthday || null,
          birthday_template_id: row.birthday_template_id || null,
          groups_ids: row.groups_ids
            ? row.groups_ids.split(",").map((id) => id.trim())
            : [],
          variables: row.variables ? JSON.parse(row.variables) : [],
          team_id: teamId,
        }));
      const { error } = await supabase
        .from("contacts")
        .insert(contactsToInsert);

      if (error) {
        throw error;
      }

      showNotification(t("importContacts.importSuccess"), "success");
      closeModal();
    } catch (error) {
      fileInputRef.current.value = null;
      showNotification(
        `${t("importContacts.importError")}: ${error.message}`,
        "error"
      );
    }
  };

  const handleBoxClick = () => {
    trackEvent("importcontacts-box_clicked");
    fileInputRef.current.click();
  };

  const closeModal = () => {
    trackEvent("importcontacts-close_modal");
    fileInputRef.current.value = null;
    onClose();
  };

  return (
    <Modal open={open} onClose={closeModal}>
      <Box sx={style}>
        <Typography variant="h5" component="h2" gutterBottom>
          {t("importContacts.importTitle")}
        </Typography>

        <Typography variant="h6" gutterBottom>
          {t("importContacts.importDescription")}{" "}
          <a
            href="https://docs.google.com/spreadsheets/d/1H7FJ6HRFfRxgOOIF5m1r5ksuV3njfW0s1sKxYk_yckc/edit?gid=0#gid=0"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("importContacts.template")}
          </a>
          , {t("importContacts.watchTutorial")}{" "}
          <a
            href={t("importContacts.tutorialUrl")}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("importContacts.tutorial")}
          </a>
          .
        </Typography>

        <Box
          onClick={handleBoxClick}
          onDrop={handleFileDrop}
          onDragOver={(e) => e.preventDefault()}
          sx={{
            border: "2px dashed rgba(121, 73, 231, 0.5)",
            p: 6,
            textAlign: "center",
            cursor: "pointer",
            borderRadius: "16px",
            background: "rgba(121, 73, 231, 0.03)",
            transition: "all 0.3s ease",
            "&:hover": {
              background: "rgba(121, 73, 231, 0.08)",
              borderColor: "#7949E7",
            },
          }}
          mt={3}
          mb={2}
        >
          <CloudUpload sx={{ fontSize: 48, color: "#7949E7", mb: 2 }} />
          <Typography variant="h6" fontWeight="bold">
            {t("importContacts.dragAndDrop")}
          </Typography>
        </Box>

        <input
          type="file"
          onChange={handleFileChange}
          ref={fileInputRef}
          hidden
        />
      </Box>
    </Modal>
  );
}
